import React, { useMemo, useState } from 'react';
import { chips } from '../../data/stack';
import { clusterSystems, knownClusters } from '../../data/clusters';
import { Chip } from '../../types/content';
import { SourceRef } from '../SourceRef';
import { ChipGroup } from '../ChipGroup';
const STATUS_STYLE: Record<Chip['status'], string> = {
  Shipping: 'border-live/40 text-live',
  Announced: 'border-machine/40 text-machine',
  Internal: 'border-human/40 text-human',
  Legacy: 'border-line text-muted'
};
export function SiliconTab() {
  const makers = useMemo(() => {
    const order: string[] = [];
    chips.forEach(c => !order.includes(c.maker) && order.push(c.maker));
    return order;
  }, []);
  const [maker, setMaker] = useState('all');
  const groups = (maker === 'all' ? makers : [maker]).map(m => ({
    maker: m,
    list: chips.filter(c => c.maker === m)
  }));
  return <>
      <section aria-labelledby="silicon-title" data-id="element-790" data-mp="1n6q4pavtyy7y">
        <h2 id="silicon-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-791" data-mp="1n5f0j9yh3ogb">
          The chips
        </h2>
        <p className="mt-2 max-w-3xl text-[14px] text-muted" data-id="element-792" data-mp="27cgmnvw511og">
          {chips.length} accelerators from {makers.length} makers, from GPUs to TPUs, Trainium, Ascend and wafer-scale chips. Memory and status come from vendor specs. Performance figures are vendor claims: we list them, we don’t endorse them.
        </p>
        <div className="mt-5" data-id="element-793" data-mp="1smj5w10jfe9a">
          <ChipGroup label="Maker" value={maker} onChange={setMaker} options={[['all', 'All'], ...makers.map(m => [m, m] as [string, string])]} data-id="element-794" data-mp="1npsjkvy53mff" />
        </div>

        <div className="mt-6 space-y-10" data-id="element-795" data-mp="1jq1ucwo9hed0">
          {groups.map(g => <div key={g.maker} data-id="element-796" data-mp="1ep4b0grime9x">
              <h3 className="flex items-baseline gap-2 text-[15px] font-medium text-ink" data-id="element-797" data-mp="19php7ixmfppi">
                {g.maker} <span className="font-mono text-[12px] text-muted" data-id="element-798" data-mp="392lr5iplc8ux">{g.list.length}</span>
              </h3>
              <div className="mt-3 overflow-x-auto rounded-lg border border-line" data-id="element-799" data-mp="343sefsi4olvt">
                <table className="w-full min-w-[1080px] border-collapse text-[13.5px]" data-id="element-800" data-mp="341prxig6pavu">
                  <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-801" data-mp="12ca40wlbvnm7">
                    <tr data-id="element-802" data-mp="176ebdgyi9xnt">
                      {['Chip', 'Class', 'Memory', 'Interconnect', 'Vendor performance claim', 'Status', ''].map((h, i) => <th key={i} scope="col" className="whitespace-nowrap border-b border-line px-3 py-2.5 font-normal" data-id="element-803" data-mp="xeqqy00d9gvu" data-mpi={i}>
                          {h || <span className="sr-only" data-id="element-804" data-mp="1l65v7ybpe89q">Link</span>}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-805" data-mp="1b76kma7do2k3">
                    {g.list.map(c => <tr key={c.name} className="border-b border-line/60 last:border-b-0" data-id="element-806" data-mp="1b915xpmk9wop">
                        <th scope="row" className="whitespace-nowrap px-3 py-3 text-left font-normal text-ink" data-id="element-807" data-mp="1g7ujfi1ulnov">
                          {c.name}
                          <SourceRef href={c.sourceUrl} label={`${c.maker} ${c.name} — vendor page`} grade="A" figure={c.name} data-id="element-808" data-mp="4qutuu6vxmyj" />
                        </th>
                        <td className="whitespace-nowrap px-3 py-3 text-muted" data-id="element-809" data-mp="2j06x5jgmv2r1">{c.chipClass}</td>
                        <td className={`px-3 py-3 font-mono text-[12.5px] ${c.memory.startsWith('Not') ? 'text-faint' : 'text-ink'}`} data-id="element-810" data-mp="2e1n6j5r3h2sc">{c.memory}</td>
                        <td className="px-3 py-3 font-mono text-[12.5px] text-ink/85" data-id="element-811" data-mp="2nyi4nnjgh163">{c.interconnect}</td>
                        <td className="whitespace-nowrap px-3 py-3 font-mono text-[12.5px]" data-id="element-812" data-mp="208ntrbip5moh">
                          {c.claim ? <span className="text-ink/85" data-id="element-813" data-mp="1v6or63us6tsl">
                              {c.claim}
                              <SourceRef href={c.sourceUrl} note="Vendor marketing claim — listed, not endorsed." data-id="element-814" data-mp="292aiwisefrop" />
                            </span> : <span className="text-faint" data-id="element-815" data-mp="1gi2b1zxbxy0b">Not listed</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3" data-id="element-816" data-mp="1p7j0zgbifrmz">
                          <span className={`rounded-full border px-2 py-0.5 font-mono text-[10.5px] ${STATUS_STYLE[c.status]}`} data-id="element-817" data-mp="1p9c9z29liny5">{c.status}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right" data-id="element-818" data-mp="1z4nn5g5cvn3s">
                          <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[12.5px] text-muted hover:text-machine" data-id="element-819" data-mp="23wx80hrm5wz7">
                            Read more →
                          </a>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>)}
        </div>
      </section>

      <section aria-labelledby="clusters-title" className="mt-16" data-id="element-820" data-mp="2mu8a0a4mr4ic">
        <h2 id="clusters-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-821" data-mp="nif9i3imvh0v">
          What makes a cluster
        </h2>
        <p className="mt-2 max-w-3xl text-[14px] text-muted" data-id="element-822" data-mp="sibcbzflh2eu">
          How chips are packaged into nodes, racks and pods. The <span className="text-ink" data-id="element-823" data-mp="kzagm0b1cd21">scale-up domain</span> is the largest group that behaves like one machine: 72 GPUs in a GB300 NVL72 rack, 9,216 chips in an Ironwood pod. Past that, systems join over a slower scale-out network.
        </p>
        <div className="mt-5 overflow-x-auto rounded-lg border border-line" data-id="element-824" data-mp="68usacnboozn">
          <table className="w-full min-w-[1180px] border-collapse text-[13px]" data-id="element-825" data-mp="66qk3iie53tk">
            <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-826" data-mp="13vtvl68isv6i">
              <tr data-id="element-827" data-mp="yyjugtbrqv2u">
                {['System', 'Maker', 'Per node / tray', 'Per rack / pod', 'Scale-up domain', 'Interconnect', 'Status'].map(h => <th key={h} scope="col" className="whitespace-nowrap border-b border-line px-3 py-2.5 font-normal" data-id="element-828" data-mp="ozcm6kjw5qml">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-829" data-mp="39p1x4mhh4sa0">
              {clusterSystems.map(s => <tr key={s.system} className="border-b border-line/60 align-top last:border-b-0" data-id="element-830" data-mp="39rwiqhc84j50">
                  <th scope="row" className="whitespace-nowrap px-3 py-3 text-left font-normal text-ink" data-id="element-831" data-mp="21o9z2qo0lmko">
                    {s.system}
                    <SourceRef href={s.sourceUrl} label={`${s.system} — vendor page`} grade="A" figure={s.system} data-id="element-832" data-mp="1rsq5iawxazz3" />
                    <span className="block font-mono text-[10.5px] text-faint" data-id="element-833" data-mp="1wpyrkml6zo5m">{s.chip}</span>
                  </th>
                  <td className="whitespace-nowrap px-3 py-3 text-muted" data-id="element-834" data-mp="1i0kgna1ywyo4">{s.maker}</td>
                  <td className="px-3 py-3 text-ink/85" data-id="element-835" data-mp="1mtnc5nqb2i6y">{s.perNode}</td>
                  <td className="px-3 py-3 text-ink" data-id="element-836" data-mp="191yz9i88fpox">{s.perRack}</td>
                  <td className="px-3 py-3 font-mono text-[12.5px] text-machine" data-id="element-837" data-mp="18zm9wk05ad1u">{s.scaleUp}</td>
                  <td className="px-3 py-3 text-muted" data-id="element-838" data-mp="1dy5vukeqnou7">{s.interconnect}</td>
                  <td className="whitespace-nowrap px-3 py-3" data-id="element-839" data-mp="z41dd1dui4zh">
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10.5px] ${STATUS_STYLE[s.status]}`} data-id="element-840" data-mp="142m48a77jcb9">{s.status}</span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="known-title" className="mt-14" data-id="element-841" data-mp="ghbmyffb5o55">
        <h2 id="known-title" className="text-[15px] font-medium text-ink" data-id="element-842" data-mp="ugbd5rigyb3f">
          Largest disclosed clusters
        </h2>
        <ul className="mt-3 grid gap-x-10 border-t border-line md:grid-cols-2" data-id="element-843" data-mp="pi04vl7to8nb">
          {knownClusters.map(k => <li key={k.name} className="border-b border-line py-3" data-id="element-844" data-mp="ze34okf7bv73">
              <p className="flex items-baseline justify-between gap-4" data-id="element-845" data-mp="zbqr0jgh86zu">
                <span className="text-[14.5px] text-ink" data-id="element-846" data-mp="1d3ww9ai3ctki">
                  {k.name}
                  <SourceRef href={k.sourceUrl} label={`${k.name} — ${k.operator}`} grade="B" figure={k.name} data-id="element-847" data-mp="1876emiqn1pwz" />
                </span>
                <span className="font-mono text-[13px] text-machine" data-id="element-848" data-mp="1n3nsphq7pin8">{k.scale}</span>
              </p>
              <p className="mt-0.5 text-[12.5px] text-muted" data-id="element-849" data-mp="1i2845fotsu03">
                {k.operator} · {k.chips} · {k.location}
              </p>
            </li>)}
        </ul>
      </section>
    </>;
}